import { useEffect, useRef, useState } from 'react';

import Checkbox from '@core/components/checkbox/Checkbox';
import RadioInput from '@core/components/radioinput/RadioInput';

import { ArrowDown2 } from 'iconsax-react';

import classes from './FilterDropdown.module.scss';

const FilterDropdown = ({
	label,
	name,
	options = [],
	value = '',
	onChange,
	type = 'checkbox',
	expanded = false,
	className = ''
}) => {
	const [open, setOpen] = useState(false);
	const wrapperRef = useRef(null);

	const selectedValues = value ? String(value).split(',').filter(Boolean) : [];

	useEffect(() => {
		if (!open) return;
		const handleClickOutside = (event) => {
			if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
				setOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [open]);

	const optionChangeHandler = (optionValue) => {
		if (type === 'radio') {
			onChange(name, optionValue);
			return;
		}
		const nextValues = selectedValues.includes(optionValue)
			? selectedValues.filter((selected) => selected !== optionValue)
			: [...selectedValues, optionValue];
		// keep the options order so the query param stays stable
		onChange(
			name,
			options
				.filter((option) => nextValues.includes(option.value))
				.map((option) => option.value)
				.join(',')
		);
	};

	const clearHandler = () => onChange(name, '');

	const selectedLabel = options.find((option) => option.value === selectedValues[0])?.label;

	const optionsList = (
		<div className={classes['options']}>
			{options.map((option) => (
				<div className={classes['option']} key={option.value}>
					{type === 'radio' ? (
						<RadioInput
							input={{ name, label: option.label, id: `${name}-${option.value}` }}
							checked={selectedValues.includes(option.value)}
							onChange={() => optionChangeHandler(option.value)}
						/>
					) : (
						<Checkbox
							input={{ name, label: option.label, id: `${name}-${option.value}` }}
							checked={selectedValues.includes(option.value)}
							onChange={() => optionChangeHandler(option.value)}
						/>
					)}
				</div>
			))}
			<button
				type="button"
				className={classes['clear-filter']}
				onClick={clearHandler}
				data-testid={`clear-filter-${name}`}>
				Clear Filter
			</button>
		</div>
	);

	if (expanded) {
		return (
			<div className={`${classes['expanded']} ${className}`}>
				<span className={classes['expanded-title']}>{label}</span>
				{optionsList}
			</div>
		);
	}

	return (
		<div ref={wrapperRef} className={`${classes['wrapper']} ${className}`}>
			<button
				type="button"
				onClick={() => setOpen((prevState) => !prevState)}
				className={`${classes['chip']} ${open ? classes['open'] : ''} ${
					selectedValues.length > 0 ? classes['active'] : ''
				}`}
				data-testid={`filter-${name}`}>
				<ArrowDown2 size={16} />
				<span>{label}</span>
				{selectedValues.length > 0 &&
					(type === 'radio' ? (
						<span className={classes['value-badge']}>{selectedLabel}</span>
					) : (
						<span className={classes['count-badge']}>{selectedValues.length}</span>
					))}
			</button>
			{open && <div className={classes['panel']}>{optionsList}</div>}
		</div>
	);
};

export default FilterDropdown;
